import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useSession } from '@/lib/useSession';

type Props = {
  children: ReactNode;
};

// In local dev (`pnpm dev:ui`), the real API isn't running, so the session
// fetch fails and the redirect to /api/v1/core/auth/login dead-ends. Skip
// the gate in dev so you can preview pages without standing up the API.
// Set VITE_AUTH_BYPASS=false in `.env.local` to test the real flow locally.
const DEV_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS !== 'false';

/**
 * Wrap protected content. If the session check returns "not authenticated"
 * the browser is sent to the in-app /login page, with the current path passed
 * as `return`. The login page asks for the user's email and routes to the
 * right identity provider (Entra for @ampersandbrands.com, Google for
 * @lolliandpops.com), then the API kicks off the OIDC flow.
 *
 * (Previously this redirected straight to the Entra login endpoint. Routing
 * through /login is what lets Lolli & Pops store staff pick the Google path.)
 *
 * While the session is loading, render a tiny placeholder. The placeholder
 * matches the page chrome so the redirect feels instant.
 */
export function AuthGate({ children }: Props) {
  const { user, isLoading, isError, isForbidden } = useSession();

  useEffect(() => {
    if (DEV_BYPASS) return;
    if (!isLoading && !isError && !isForbidden && user == null) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      const params = new URLSearchParams({ return: returnTo });
      window.location.assign(`/login?${params.toString()}`);
    }
  }, [user, isLoading, isError, isForbidden]);

  if (DEV_BYPASS) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amp-cream text-sm text-amp-700 dark:bg-amp-dark-bg dark:text-amp-dark-ink-muted">
        Checking your session...
      </div>
    );
  }

  if (isForbidden) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-amp-cream text-amp-700 dark:bg-amp-dark-bg dark:text-amp-dark-ink-muted">
        <p className="text-base font-medium">Access denied</p>
        <p className="text-sm">
          You&apos;re signed in but don&apos;t have access to Cockpit. Contact IT to request
          access.
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amp-cream text-sm text-red-700 dark:bg-amp-dark-bg dark:text-red-300">
        Could not reach the API. Reload to retry.
      </div>
    );
  }

  if (user == null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amp-cream text-sm text-amp-700 dark:bg-amp-dark-bg dark:text-amp-dark-ink-muted">
        Redirecting you to sign in...
      </div>
    );
  }

  return <>{children}</>;
}
